import i18n from '@/i18n'
import router from '@/router'
import { MediaUtils } from '@/utils/mediaUtils'
import axios from 'axios'
import { orderBy } from 'lodash'
import { apiV3Segment } from '../../shared/constants'
import { ErrorUtils } from '../../utils/errorUtils'

const getDefaultState = () => {
  return {
    media: [],
    media_loaded: false,
    sharedMedia: [],
    sharedMedia_loaded: false,
    modalDesktopsShow: false,
    mediaId: '',
    mediaDesktops: []
  }
}

const state = getDefaultState()

export default {
  state,
  getters: {
    getMedia: state => {
      return state.media
    },
    getSharedMedia: state => {
      return state.sharedMedia
    },
    getMediaLoaded: state => {
      return state.media_loaded
    },
    getSharedMediaLoaded: state => {
      return state.sharedMedia_loaded
    },
    getShowDeleteMediaModal: state => {
      return state.modalDesktopsShow
    },
    getMediaId: state => {
      return state.mediaId
    },
    getMediaDesktops: state => {
      return state.mediaDesktops
    }
  },
  mutations: {
    resetMediaState: (state) => {
      Object.assign(state, getDefaultState())
    },
    setMedia: (state, media) => {
      state.media = media
      state.media_loaded = true
    },
    setSharedMedia: (state, media) => {
      state.sharedMedia = media
      state.sharedMedia_loaded = true
    },
    add_media: (state, media) => {
      // Idempotent — see add_desktop in modules/desktops.js.
      const existingIndex = state.media.findIndex(d => d.id === media.id)
      if (existingIndex === -1) {
        state.media = [...state.media, media]
      } else {
        state.media = state.media.map(d => d.id === media.id ? { ...d, ...media } : d)
      }
    },
    update_media: (state, media) => {
      const item = state.media.find(d => d.id === media.id)
      if (item) {
        Object.assign(item, media)
      }
    },
    remove_media: (state, media) => {
      const mediaIndex = state.media.findIndex(d => d.id === media.id)
      if (mediaIndex !== -1) {
        state.media.splice(mediaIndex, 1)
      }
    },
    setShowDeleteMediaModal: (state, modalShow) => {
      state.modalDesktopsShow = modalShow
    },
    setMediaId: (state, mediaId) => {
      state.mediaId = mediaId
    },
    setMediaDesktops: (state, desktops) => {
      state.mediaDesktops = desktops
    }
  },
  actions: {
    socket_mediaAdd (context, data) {
      const media = MediaUtils.parseMedia(JSON.parse(data))
      context.commit('add_media', media)
    },
    socket_mediaUpdate (context, data) {
      // Partial-row merge — keep keys present in the payload so the
      // exclude_none=True change-handler emit doesn't overwrite
      // cached fields with undefined.
      const media = MediaUtils.parseMedia(JSON.parse(data), { partial: true })
      context.commit('update_media', media)
    },
    socket_mediaProgress (context, data) {
      // The download tick never reaches the row, so it arrives on its own
      // event carrying only the counters: merge it into the cached one.
      context.commit('update_media', JSON.parse(data))
    },
    socket_mediaDelete (context, data) {
      const media = JSON.parse(data)
      context.commit('remove_media', media)
    },
    resetMediaState (context) {
      context.commit('resetMediaState')
    },
    fetchMedia ({ commit }) {
      axios.get(`${apiV3Segment}/items/media`).then(response => {
        commit(
          'setMedia',
          MediaUtils.parseMediaList(orderBy(response.data.media, ['desc']))
        )
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    fetchSharedMedia ({ commit }) {
      // get-shared returns the full list as { media: [...] }. The
      // get-allowed endpoint is paginated (page_size 1-50) and would silently
      // cap shared items at 10 for the user.
      axios.get(`${apiV3Segment}/items/media/get-shared`).then(response => {
        commit(
          'setSharedMedia',
          MediaUtils.parseMediaList(orderBy(response.data.media, ['desc']))
        )
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    createNewMedia (_, payload) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.creating-media'), '', true, 1000)

      axios.post(`${apiV3Segment}/item/media`, payload).then(response => {
        router.push({ name: 'media' })
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    fetchMediaDesktops (context, data) {
      axios.get(`${apiV3Segment}/item/media/${data.mediaId}/get-desktops`).then(response => {
        if (response.data.length > 0) {
          context.commit('setMediaDesktops', MediaUtils.parseMediaDesktops(response.data))
          context.commit('setMediaId', data.mediaId)
          context.dispatch('showDeleteMediaModal', true)
        } else {
          const yesAction = () => {
            context.dispatch('deleteMedia', data.mediaId)
            this._vm.$snotify.clear()
          }

          const noAction = (toast) => {
            this._vm.$snotify.clear()
          }

          this._vm.$snotify.prompt(`${i18n.t('messages.confirmation.delete-media', { name: data.name })}`, {
            position: 'centerTop',
            buttons: [
              { text: `${i18n.t('messages.yes')}`, action: yesAction, bold: true },
              { text: `${i18n.t('messages.no')}`, action: noAction }
            ],
            placeholder: ''
          })
        }
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    showDeleteMediaModal (context, show) {
      context.commit('setShowDeleteMediaModal', show)
    },
    deleteMedia (_, mediaId) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.deleting-media'))

      axios.delete(`${apiV3Segment}/item/media/${mediaId}`).then(response => {
        this._vm.$snotify.clear()
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    downloadMedia (_, mediaId) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.downloading-media'))

      axios.put(`${apiV3Segment}/item/media/${mediaId}/download`).then(response => {
        this._vm.$snotify.clear()
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    },
    stopMediaDownload (_, mediaId) {
      ErrorUtils.showInfoMessage(this._vm.$snotify, i18n.t('messages.info.stopping-download'))

      axios.put(`${apiV3Segment}/item/media/${mediaId}/abort`).then(response => {
        this._vm.$snotify.clear()
      }).catch(e => {
        ErrorUtils.handleErrors(e, this._vm.$snotify)
      })
    }
  }
}
